import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, of, timer } from 'rxjs';
import { catchError, finalize, map, retry, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export const HEALTH_CHECK_RETRY_COUNT = 5;
export const HEALTH_CHECK_RETRY_DELAY_MS = 2000;

/**
 * Probes the gateway health endpoint.
 *
 * `probe()` emits `true` as soon as the endpoint answers, or `false` once the
 * initial attempt plus HEALTH_CHECK_RETRY_COUNT retries (HEALTH_CHECK_RETRY_DELAY_MS
 * apart) have all failed. It never errors. Concurrent callers share the same
 * in-flight probe, so a burst of failing requests triggers a single health check.
 *
 * Uses HttpBackend so the probe bypasses every interceptor (no auth header, and
 * no recursion through the error handling of AppHttpInterceptor).
 */
@Injectable({
  providedIn: 'root',
})
export class HealthCheckService {
  private readonly http: HttpClient;
  private inFlight$: Observable<boolean> | null = null;

  constructor(httpBackend: HttpBackend) {
    this.http = new HttpClient(httpBackend);
  }

  isHealthCheckUrl(url: string): boolean {
    return !!environment.healthCheckURL && url.startsWith(environment.healthCheckURL);
  }

  probe(): Observable<boolean> {
    if (!this.inFlight$) {
      this.inFlight$ = this.http.get(environment.healthCheckURL, { withCredentials: true }).pipe(
        retry({ count: HEALTH_CHECK_RETRY_COUNT, delay: () => timer(HEALTH_CHECK_RETRY_DELAY_MS) }),
        map(() => true),
        catchError((err) => {
          console.error(err, 'Health check failed');
          return of(false);
        }),
        finalize(() => {
          this.inFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.inFlight$;
  }
}
